/**
 * Naming_Flow reducer (pure).
 *
 * Implements the state machine in the design document's "Naming_Flow
 * Engine" section as a single pure function. The reducer takes a
 * `NamingState` and a `NamingEvent` and returns a new `NamingState`
 * without mutating the input. The function is total: every (state,
 * event) pair returns a valid `NamingState`; any combination not listed
 * in the transition table falls through to returning the input state
 * unchanged.
 *
 * The `suggested` set uses array semantics with deduplication
 * (`[...new Set([...suggested, name])]`) to preserve insertion order
 * while ensuring distinctness. This is the basis of Property 33.
 *
 * Property 32 (transitions match the state diagram) and Property 33
 * (suggested-name set is monotone and distinct) are validated by the
 * tests in `src/__tests__/properties/naming-flow.property.test.ts`.
 *
 * Validates: Requirements 16.4, 16.5, 16.6, 16.10, 16.12.
 */

import type { NamingEvent, NamingState } from './types';

/**
 * Append `name` to `suggested` if not already present, preserving
 * insertion order. Always returns a fresh array — never mutates input.
 */
function addSuggested(suggested: readonly string[], name: string): string[] {
  return [...new Set([...suggested, name])];
}

/**
 * Compute the initial Naming_Flow state for a fresh boot.
 *
 * - If a non-empty `persistedName` is supplied, the flow is effectively
 *   already complete; we return `idle` so the orchestrator does not
 *   render naming UI on subsequent boots.
 * - If `persistedName === null`, we return `awaitingFirstReply` with an
 *   empty `suggested` set so the AI's first reply will populate the
 *   first proposal.
 *
 * Note: an empty string is treated the same as `null` to satisfy the
 * "non-empty" guard implied by the design (the persisted column may
 * round-trip an empty string through legacy storage).
 *
 * Validates: Requirements 16.1, 16.10.
 *
 * @param persistedName The AI_Companion_Name read from the
 *   Conversation_Store, or `null` if none has been persisted yet.
 * @returns The initial `NamingState` for the boot sequence.
 */
export function initialNamingState(persistedName: string | null): NamingState {
  if (typeof persistedName === 'string' && persistedName.length > 0) {
    return { phase: 'idle' };
  }
  return { phase: 'awaitingFirstReply', suggested: [] };
}

/**
 * Reduce a `NamingState` with a `NamingEvent` and return the next state.
 *
 * The reducer is pure: it does not perform IO, observe time, or mutate
 * its arguments. New objects/arrays are constructed for any state
 * change. `wipeAll` is the universal reset and takes precedence over
 * the per-phase transition table.
 *
 * Transition table (anything not listed returns `state` unchanged):
 *
 * | From phase            | Event                  | To phase                                                                 |
 * |-----------------------|------------------------|--------------------------------------------------------------------------|
 * | any                   | wipeAll                | awaitingFirstReply{ suggested: [] }                                      |
 * | awaitingFirstReply    | aiProposed{name}       | awaitingUserResponse{ suggested ∪ {name}, lastProposed: name }           |
 * | awaitingUserResponse  | aiProposed{name}       | awaitingUserResponse{ suggested ∪ {name}, lastProposed: name }           |
 * | clarifying            | aiProposed{name}       | awaitingUserResponse{ suggested ∪ {name}, lastProposed: name }           |
 * | awaitingUserResponse  | userAccepts            | confirmed{ name: lastProposed }                                          |
 * | clarifying            | userAccepts            | confirmed{ name: lastProposed } (user clarified to accept)               |
 * | awaitingUserResponse  | userProvidesCustom{n}  | confirmed{ name: n }                                                     |
 * | clarifying            | userProvidesCustom{n}  | confirmed{ name: n }                                                     |
 * | awaitingUserResponse  | userRequestsAnother    | awaitingUserResponse (unchanged; awaits next aiProposed)                 |
 * | clarifying            | userRequestsAnother    | awaitingUserResponse{ suggested, lastProposed } (awaits next aiProposed) |
 * | awaitingUserResponse  | userAmbiguous          | clarifying{ suggested, lastProposed }                                    |
 * | clarifying            | userAmbiguous          | clarifying (unchanged; AI may re-clarify)                                |
 * | idle                  | (any non-wipeAll)      | unchanged                                                                |
 * | confirmed             | (any non-wipeAll)      | unchanged                                                                |
 *
 * Validates: Requirements 16.4, 16.5, 16.6, 16.10, 16.12 — and the
 * universal claims expressed by Property 32 and Property 33.
 *
 * @param state The current `NamingState`. Treated as immutable.
 * @param event The `NamingEvent` to apply.
 * @returns The next `NamingState`. Always a fresh object on a state
 *   change; the same reference as `state` on a no-op transition.
 */
export function reduceNaming(state: NamingState, event: NamingEvent): NamingState {
  // wipeAll is the universal reset and takes precedence over the
  // per-phase table (Requirement 16.10, Property 32).
  if (event.kind === 'wipeAll') {
    return { phase: 'awaitingFirstReply', suggested: [] };
  }

  switch (state.phase) {
    case 'idle': {
      // The flow is dormant; only wipeAll (handled above) reactivates it.
      return state;
    }

    case 'awaitingFirstReply': {
      if (event.kind === 'aiProposed') {
        return {
          phase: 'awaitingUserResponse',
          suggested: addSuggested(state.suggested, event.name),
          lastProposed: event.name,
        };
      }
      // No other events are meaningful before the AI has proposed a
      // first name; remain in awaitingFirstReply.
      return state;
    }

    case 'awaitingUserResponse': {
      switch (event.kind) {
        case 'aiProposed':
          // Reached after a userRequestsAnother cycle: the AI proposes
          // again. Add to suggested, update lastProposed.
          return {
            phase: 'awaitingUserResponse',
            suggested: addSuggested(state.suggested, event.name),
            lastProposed: event.name,
          };
        case 'userAccepts':
          return { phase: 'confirmed', name: state.lastProposed };
        case 'userProvidesCustom':
          return { phase: 'confirmed', name: event.name };
        case 'userRequestsAnother':
          // Structurally unchanged — we still await the next
          // aiProposed event. Return the same reference to make the
          // no-op explicit and cheap.
          return state;
        case 'userAmbiguous':
          return {
            phase: 'clarifying',
            suggested: [...state.suggested],
            lastProposed: state.lastProposed,
          };
        default:
          return state;
      }
    }

    case 'clarifying': {
      switch (event.kind) {
        case 'aiProposed':
          // The AI clarified by proposing another name; resume the
          // normal awaitingUserResponse phase with the new proposal.
          return {
            phase: 'awaitingUserResponse',
            suggested: addSuggested(state.suggested, event.name),
            lastProposed: event.name,
          };
        case 'userAccepts':
          // The user clarified that they want the lastProposed name.
          // Confirm it (the reducer is robust to this transition even
          // though the canonical path goes through awaitingUserResponse).
          return { phase: 'confirmed', name: state.lastProposed };
        case 'userProvidesCustom':
          return { phase: 'confirmed', name: event.name };
        case 'userRequestsAnother':
          // The user clarified that they want another suggestion;
          // return to awaitingUserResponse and await the next
          // aiProposed event.
          return {
            phase: 'awaitingUserResponse',
            suggested: [...state.suggested],
            lastProposed: state.lastProposed,
          };
        case 'userAmbiguous':
          // Still ambiguous — remain clarifying so the AI may re-ask.
          return state;
        default:
          return state;
      }
    }

    case 'confirmed': {
      // The flow has settled. Only wipeAll (handled above) restarts it.
      return state;
    }

    default: {
      // Exhaustiveness check — every NamingState phase must be handled.
      const _exhaustive: never = state;
      return _exhaustive;
    }
  }
}
